// weeklyChecks.js — non-daily health-tracking reminders that appear in the
// Calories Log banner alongside the supplement reminder.
//
// Two checks currently:
//   1. Mounjaro (GLP-1) weekly dose — fires when the last logged dose was
//      ≥7 days ago, or when no dose has been logged in the last 14 days
//      (covers a missed week or a fresh cycle).
//   2. Weigh-in — fires when no weight has been recorded in the last 7
//      days, either manually (calorieWeightLog) or via Apple Health
//      (calorieLog[*].bodyMassKg / bodyHistory.weight samples).
//
// Each check is a `{ id, emoji, label, color, action, ... }` shape that
// WeeklyChecksBanner renders as a chip. Action types:
//   - 'logEntry'  → logs a pre-defined entry into today's supplements slot
//   - 'weighIn'   → prompts for kg + saves to calorieWeightLog
//
// Scheduling is intentionally minimal: a single daily local notification
// at the same time as the supplement reminder, with copy that mentions
// the GLP-1 + weigh-in side of the check.

import { LocalNotifications } from '@capacitor/local-notifications';
import { getMounjaroDay, hasAnyMedsMounjaro } from './mounjaroCycle';

const TIME_PREF_KEY = 'sirat.hayah.weeklyChecksReminderTime';
const DISMISS_KEY   = 'sirat.hayah.weeklyChecksDismissed';
const NOTIF_ID      = 9002;            // distinct from supplements (9001)
const DEFAULT_TIME  = '09:00';         // morning is better for weigh-in fasted

const MOUNJARO_INTERVAL_DAYS = 7;
const MOUNJARO_GRACE_DAYS    = 14;     // no dose at all in the last 14d = stale
const WEIGHIN_MAX_AGE_DAYS   = 7;

// Pre-defined Mounjaro entry — added to today's supplements slot when
// the user taps the GLP-1 chip. Matches the /mounjaro|tirzepatide/i
// pattern getMounjaroDay() scans for, so the cycle resets automatically.
export const MOUNJARO_DEF = {
  id: 'mounjaro',
  emoji: '💉',
  label: 'GLP-1 dose',
  color: '#9e7cd5',
  base: {
    brand: "Eli Lilly", name: "Mounjaro (tirzepatide) weekly dose",
    category: "Medication",
    calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0, sodium: 0,
    servingG: 0, servingLabel: "1 injection", halal: true,
  },
  servingG: 0,
  servingLabel: "1 injection",
  toast: "💉 Mounjaro dose logged",
};

const WEIGHIN_DEF = {
  id: 'weighIn',
  emoji: '⚖️',
  label: 'Weigh-in',
  color: '#5fb8a8',
  toast: (kg) => `⚖️ Weight logged: ${kg} kg`,
};

// ───────────────────────────────────────────────────────────────────────
// Detection
// ───────────────────────────────────────────────────────────────────────

function localDateStr(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// True when the user is due for their weekly Mounjaro dose. Stale = no
// dose in 14d. Due = on cycle day 7 (mounjaroCycle.dayOfCycle reaches 7
// once daysSince > 6, i.e. anywhere in the 7th day after the last dose).
//
// We use dayOfCycle (Math.ceil of daysSince) rather than `daysSince >= 7`
// because the user thinks of "day 7 = dose day" — if they dosed at 09:30
// last Saturday and check at 09:00 this Saturday, daysSince is 6.98 but
// it's still their dose day. The strict full-7-day floor missed that.
export function isMounjaroDue(calorieLog, todayKey = localDateStr()) {
  const m = getMounjaroDay(calorieLog);
  if (!m) {
    // No dose logged in the last 14d. We *don't* want to spam fresh users
    // who don't use Mounjaro at all, so only flag when the user has EVER
    // logged one — in calorieLog OR the Medications tracker.
    return hasAnyHistoricMounjaroEntry(calorieLog) || hasAnyMedsMounjaro();
  }
  return m.dayOfCycle >= MOUNJARO_INTERVAL_DAYS;
}

function hasAnyHistoricMounjaroEntry(calorieLog) {
  if (!calorieLog || typeof calorieLog !== "object") return false;
  for (const dayLog of Object.values(calorieLog)) {
    const meals = dayLog?.meals || {};
    for (const entries of Object.values(meals)) {
      if (!Array.isArray(entries)) continue;
      for (const e of entries) {
        if (/mounjaro|tirzepatide/i.test(String(e?.name || ""))) return true;
      }
    }
  }
  return false;
}

// Returns the timestamp (ms) of the most recent known weight reading
// across manual logs + HealthKit pulls, or null if none in 30d. Used to
// decide whether a weigh-in reminder should fire today.
//
// IMPORTANT: only trust sources that carry the *original* sample date.
// calorieLog[date].bodyMassKg is overwritten by readAppleHealth with the
// latest HealthKit reading regardless of when that reading was taken —
// so a 14-day-old sample lands in today's slot and looks fresh. Skip it.
// Instead, scan bodyHistory.weight (HealthKit raw samples with their
// real dates) across whatever calorieLog day holds it (it's rewritten
// each sync, but the array still carries true sample dates).
export function getLatestWeightTs(calorieLog, weightLog) {
  let latest = null;
  // 1. Manual entries.
  if (Array.isArray(weightLog)) {
    for (const w of weightLog) {
      const ts = w.date
        ? Date.parse(`${w.date}T12:00:00`)
        : (w.ts || null);
      if (Number.isFinite(ts) && (!latest || ts > latest)) latest = ts;
    }
  }
  // 2. HealthKit raw samples with their true recorded dates. Scan every
  // day's bodyHistory.weight so we pick up the freshest sync even if the
  // user hasn't opened Calories today.
  if (calorieLog && typeof calorieLog === "object") {
    for (const dayLog of Object.values(calorieLog)) {
      const bh = dayLog?.bodyHistory?.weight;
      if (!Array.isArray(bh)) continue;
      for (const s of bh) {
        const ts = s.date ? Date.parse(s.date) : null;
        if (Number.isFinite(ts) && (!latest || ts > latest)) latest = ts;
      }
    }
  }
  return latest;
}

export function isWeighInDue(calorieLog, weightLog) {
  const latest = getLatestWeightTs(calorieLog, weightLog);
  if (!latest) return true; // never logged → always due
  const daysSince = (Date.now() - latest) / 86400000;
  return daysSince >= WEIGHIN_MAX_AGE_DAYS;
}

// Returns the array of due checks for today. Each entry is the def
// extended with `action` so the banner knows how to handle the tap.
export function getDueWeeklyChecks(calorieLog, weightLog, todayKey = localDateStr()) {
  const due = [];
  if (isMounjaroDue(calorieLog, todayKey)) {
    due.push({ ...MOUNJARO_DEF, action: 'logEntry' });
  }
  if (isWeighInDue(calorieLog, weightLog)) {
    const latest = getLatestWeightTs(calorieLog, weightLog);
    const ageDays = latest ? Math.floor((Date.now() - latest) / 86400000) : null;
    due.push({
      ...WEIGHIN_DEF,
      action: 'weighIn',
      ageDays,
      subtitle: ageDays == null ? "no recent reading" : `last ${ageDays}d ago`,
    });
  }
  return due;
}

// ───────────────────────────────────────────────────────────────────────
// Prefs + dismissal (per-day so the banner re-appears tomorrow)
// ───────────────────────────────────────────────────────────────────────

export function getReminderTime() {
  try { return localStorage.getItem(TIME_PREF_KEY) || DEFAULT_TIME; } catch { return DEFAULT_TIME; }
}
export function setReminderTime(t) {
  try { localStorage.setItem(TIME_PREF_KEY, t); } catch {}
  return scheduleDailyReminder();
}

export function isPastReminderTime() {
  const t = getReminderTime();
  const [hh, mm] = t.split(':').map(n => parseInt(n, 10));
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return false;
  const now = new Date();
  return now.getHours() * 60 + now.getMinutes() >= hh * 60 + mm;
}

export function isDismissedToday(todayKey) {
  try { return localStorage.getItem(DISMISS_KEY) === todayKey; } catch { return false; }
}
export function dismissToday(todayKey) {
  try { localStorage.setItem(DISMISS_KEY, todayKey); } catch {}
}

// ───────────────────────────────────────────────────────────────────────
// iOS local notifications
// ───────────────────────────────────────────────────────────────────────

function isNative() {
  return !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
}

export async function ensurePermission() {
  if (!isNative()) return false;
  try {
    const status = await LocalNotifications.checkPermissions();
    if (status?.display === 'granted') return true;
    const req = await LocalNotifications.requestPermissions();
    return req?.display === 'granted';
  } catch { return false; }
}

function readJSON(key, fallback) {
  try { const v = JSON.parse(localStorage.getItem(key)); return v == null ? fallback : v; }
  catch { return fallback; }
}

// Build notification copy from what's ACTUALLY due — never a generic
// "GLP-1 dose due? Weigh-in pending?" that nags about things already done.
function reminderBody(due) {
  const parts = due.map(d => {
    if (d.id === 'mounjaro') return '💉 GLP-1 weekly dose is due';
    if (d.id === 'weighIn')  return d.ageDays != null ? `⚖️ Weigh-in (last ${d.ageDays}d ago)` : '⚖️ Weigh-in pending';
    return d.label;
  });
  return `${parts.join(' · ')}. Tap to log.`;
}

// Schedule a SINGLE upcoming reminder reflecting current due state, instead of
// a blind daily repeat. If nothing is due, cancel — no notification. Fires at
// the next occurrence of the reminder time; re-evaluated whenever the user
// opens Calories or logs a dose/weigh-in, so it stops nagging the moment the
// task is done.
export async function scheduleDailyReminder() {
  if (!isNative()) return false;
  try {
    const t = getReminderTime();
    const [hh, mm] = t.split(':').map(n => parseInt(n, 10));
    if (!Number.isFinite(hh) || !Number.isFinite(mm)) return false;
    try { await LocalNotifications.cancel({ notifications: [{ id: NOTIF_ID }] }); } catch {}

    const due = getDueWeeklyChecks(readJSON('calorieLog', {}), readJSON('calorieWeightLog', []));
    if (!due.length) return true; // nothing pending → stay silent

    const now = new Date();
    const at = new Date(now); at.setHours(hh, mm, 0, 0);
    if (at <= now) at.setDate(at.getDate() + 1); // next occurrence

    await LocalNotifications.schedule({
      notifications: [{
        id: NOTIF_ID,
        title: '🩺 Weekly check',
        body: reminderBody(due),
        schedule: { at, allowWhileIdle: true },
      }],
    });
    return true;
  } catch { return false; }
}

let _rescheduleHooked = false;
export async function initWeeklyChecksReminders() {
  if (!isNative()) return;
  const ok = await ensurePermission();
  if (ok) await scheduleDailyReminder();
  // Re-evaluate the moment a dose is logged in the Medications tracker (or any
  // weekly-check state changes) so a just-taken dose silences the reminder.
  if (!_rescheduleHooked) {
    _rescheduleHooked = true;
    const reschedule = () => { scheduleDailyReminder().catch(() => {}); };
    try {
      window.addEventListener('medications-updated', reschedule);
      window.addEventListener('weeklyChecks-updated', reschedule);
    } catch {}
  }
}
